import * as React from 'react';
import { CodeComponent } from './components/code-component/code-component.comp';
import { CollapsibleComponent } from './components/collapsible-component/collapsible-component.comp';
import { HeadingComponent } from './components/heading-component/heading-component.comp';
import { HtmlComponent } from './components/html-component/html-component.comp';
import { IframeComponent } from './components/iframe-component/iframe-component.comp';
import { ImageComponent } from './components/image-component/image-component.comp';
import { InformativeComponent } from './components/informative-component/informative-component.comp';
import { LineComponent } from './components/line-component/line-component.comp';
import { MarkdownComponent } from './components/markdown-component/markdown-component.comp';
import { QuestionComponent } from './components/question-component/question-component.comp';
import { StepByStepComponent } from './components/step-by-step-component/step-by-step-component.comp';
import { TableComponent } from './components/table-component/table-component.comp';
import { TabsComponent } from './components/tabs-component/tabs-component.comp';
import { TextComponent } from './components/text-component/text-component.comp';
import { VideoComponent } from './components/video-component/video-component.comp';
import { namespacedClassnames } from '../utils/namespace-classes';
import { getLastListItemCount } from './helpers';
import { calcDirection } from '../utils/calc-direction';
import { classNames } from '@wix/answers-lib';
export class ArticleViewer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            textCompListOffsets: {}
        };
        this.getRenderableComponents = () => this.props.components.filter((component) => component.type !== 'ai_component');
        this.contentListUpdate = () => {
            const components = this.getRenderableComponents();
            const textCompListOffsets = components.reduce((acc, _, idx) => {
                const listOffset = getLastListItemCount(components, idx);
                return Object.assign(Object.assign({}, acc), { [idx]: listOffset });
            }, {});
            this.setState({ textCompListOffsets });
        };
        this.renderArticleComponent = (articleComponent, idx) => {
            const renderedComponent = this.renderComponent(articleComponent.type, articleComponent.value, idx);
            if (!renderedComponent) {
                return null;
            }
            return this.props.componentWrapper ? (this.props.componentWrapper(renderedComponent, articleComponent)) : renderedComponent;
        };
        this.mapThemeToClassName = () => {
            switch (this.props.theme) {
                case 'helpcenter':
                    return 'helpcenter-custom-style';
                case 'widget':
                    return 'widget-custom-style';
                default:
                    return '';
            }
        };
    }
    componentDidMount() {
        const { onLinkClick } = this.props;
        this.contentListUpdate();
        if (onLinkClick) {
            const linkElements = document.body.querySelectorAll('.article-viewer a');
            Array.from(linkElements).forEach((link) => {
                const path = link.getAttribute('href') || '';
                link.addEventListener('click', (e) => onLinkClick(e, path));
            });
        }
    }
    componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps.components) !== JSON.stringify(this.props.components)) {
            this.contentListUpdate();
        }
    }
    renderComponent(type, value, idx) {
        var _a, _b;
        const props = this.props;
        const listOffset = this.state.textCompListOffsets[idx] || 0;
        const direction = calcDirection(props.locale);
        switch (type) {
            case 'text':
                return React.createElement(TextComponent, { key: idx, value: value, listOffset: listOffset });
            case 'line':
                return React.createElement(LineComponent, { key: idx, value: value });
            case 'image':
                const maybeOnImgClick = props.onImgClick ? props.onImgClick.bind(this) : undefined;
                return React.createElement(ImageComponent, { key: idx, value: value, onImgClick: maybeOnImgClick, index: idx });
            case 'heading':
                return React.createElement(HeadingComponent, { key: idx, value: value });
            case 'informative':
                return React.createElement(InformativeComponent, { key: idx, value: value, direction: direction });
            case 'video':
                return React.createElement(VideoComponent, { key: idx, value: value, direction: direction });
            case 'code':
                return React.createElement(CodeComponent, { key: idx, value: value });
            case 'html':
                return React.createElement(HtmlComponent, { key: idx, value: value });
            case 'collapsible':
                return React.createElement(CollapsibleComponent, { key: idx, value: value });
            case 'iframe':
                return React.createElement(IframeComponent, { key: idx, value: value });
            case 'step-by-step':
                return React.createElement(StepByStepComponent, { key: idx, value: value, locale: props.locale });
            case 'markdown':
                return React.createElement(MarkdownComponent, { key: idx, value: value });
            case 'table':
                return React.createElement(TableComponent, { key: idx, value: value, direction: direction });
            case 'tabs':
                return React.createElement(TabsComponent, { key: idx, value: value, onTabClick: (_a = props.tabsProps) === null || _a === void 0 ? void 0 : _a.onTabClick, selectedTabsOnLoad: (_b = props.tabsProps) === null || _b === void 0 ? void 0 : _b.selectedTabsOnLoad, locale: props.locale, direction: direction });
            case 'question':
                return React.createElement(QuestionComponent, { key: idx, value: value, locale: props.locale, onQuestionClick: props.onQuestionClick });
            // Skipping beats throwing: one block this viewer has no case for must not blank the article.
            default:
                return null;
        }
    }
    render() {
        const components = this.getRenderableComponents();
        const direction = calcDirection(this.props.locale);
        const viewerClassNames = namespacedClassnames('article-viewer', direction);
        const wrapperClassNames = classNames(this.mapThemeToClassName(), { 'article-viewer-dark-theme': !!this.props.darkTheme });
        return (React.createElement("div", { className: wrapperClassNames },
            React.createElement("div", { className: viewerClassNames, lang: this.props.locale }, components.map(this.renderArticleComponent))));
    }
}
